import React, { FC, useEffect, useState, useCallback } from "react";
import apiInstance from "../services/httpService";
import paymentService from "../services/paymentService";

// ── Types ────────────────────────────────────────────────────────────────────

interface SubEvent {
  event_id:        number;
  name:            string;
  display_name:    string;
  description:     string;
  event_thumbnail: string | null;
  payment_details: {
    registration_status: string;
    pending_amount:      number;
    charge_type:         string;
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const YEAR = new Date().getFullYear();
// Subscription events are named exactly "{YEAR}-1" and "{YEAR}-2".
const SUB_PATTERN = /^\d{4}-[12]$/;

const HALF_LABEL: Record<string, string> = {
  [`${YEAR}-1`]: "1st Half",
  [`${YEAR}-2`]: "2nd Half",
};

const fmt = (n: number) =>
  n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

// ── Component ────────────────────────────────────────────────────────────────

const SubscriptionBanner: FC = () => {
  const [subEvents, setSubEvents]   = useState<SubEvent[]>([]);
  const [paying,    setPaying]      = useState<number | null>(null);
  const [notice,    setNotice]      = useState<{ text: string; ok: boolean } | null>(null);

  const fetchStatus = useCallback(() => {
    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall("api/ccc/user/dashboard", { token }, "POST")
      .then((res: any) => {
        if (res?.data?.status === "success") {
          const events: SubEvent[] = (res.data.data?.events || []).filter(
            (e: SubEvent) => SUB_PATTERN.test(e.name)
          );
          // Sort H1 before H2
          events.sort((a, b) => a.name.localeCompare(b.name));
          setSubEvents(events);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (subEvents.length === 0) return null;

  const initiatePayment = async (ev: SubEvent) => {
    setPaying(ev.event_id);
    setNotice(null);
    const token = localStorage.getItem("token");
    try {
      const startRes: any = await apiInstance.axiosCall(
        "api/ccc/event/payment/start",
        { token, data: { payment_amount: ev.payment_details.pending_amount, event_id: ev.event_id } },
        "POST"
      );
      if (startRes?.data?.status !== "success") {
        setNotice({ text: startRes?.data?.message || "Could not start payment.", ok: false });
        setPaying(null);
        return;
      }

      const td = startRes.data.data.transaction_details;
      const returnData = {
        eventId:       ev.event_id,
        transactionId: td.transaction_id,
        orderId:       td.order_id,
        amount:        ev.payment_details.pending_amount,
      };

      await paymentService.createRazorpayInstance(
        ev.payment_details.pending_amount,
        ev.display_name,
        ev.description || ev.display_name,
        ev.event_thumbnail,
        td.email,
        td.phone,
        async (response: any, other: any) => {
          const completeRes: any = await apiInstance.axiosCall(
            "api/ccc/event/payment/complete",
            {
              token,
              data: {
                status:         "success",
                payment_amount: other.amount,
                transaction_id: other.transactionId,
                razorpay_id:    response.razorpay_payment_id,
                event_id:       other.eventId,
                order_id:       other.orderId,
              },
            },
            "POST"
          );
          if (completeRes?.data?.status === "success") {
            setNotice({ text: "Payment successful! Subscription confirmed.", ok: true });
            fetchStatus();
          } else {
            setNotice({
              text: completeRes?.data?.message || "Payment could not be confirmed. Contact support.",
              ok: false,
            });
          }
          setPaying(null);
        },
        returnData
      );
    } catch {
      setNotice({ text: "Payment failed. Please check your connection and try again.", ok: false });
      setPaying(null);
    }
  };

  return (
    <div style={{
      padding: "0.2rem 1.5rem 0.6rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.35rem",
    }}>
      {/* Compact pill buttons */}
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
        {subEvents.map((ev) => {
          const paid   = ev.payment_details.registration_status === "confirmed";
          const amount = ev.payment_details.pending_amount;
          const half   = HALF_LABEL[ev.name] || ev.display_name;
          const busy   = paying === ev.event_id;

          return (
            <button
              key={ev.event_id}
              disabled={paid || busy}
              onClick={() => !paid && !busy && initiatePayment(ev)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 14px",
                borderRadius: "20px",
                border: "none",
                cursor: paid || busy ? "default" : "pointer",
                background: paid ? "#2e7d32" : "#c62828",
                color: "#fff",
                boxShadow: paid
                  ? "0 2px 6px rgba(46,125,50,0.3)"
                  : "0 2px 6px rgba(198,40,40,0.3)",
                fontFamily: "Montserrat-Regular",
                fontSize: "0.88rem",
                whiteSpace: "nowrap",
                opacity: busy ? 0.75 : 1,
                transition: "opacity 0.2s, filter 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!paid && !busy)
                  (e.currentTarget as HTMLButtonElement).style.filter = "brightness(1.1)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.filter = "none";
              }}
            >
              {/* Dot indicator */}
              <span style={{
                width: "7px", height: "7px", borderRadius: "50%",
                background: "rgba(255,255,255,0.7)",
                flexShrink: 0,
              }} />

              {/* Text */}
              <span>
                <span style={{ fontFamily: "Montserrat-SemiBold" }}>
                  {half} {YEAR}
                </span>
                {" · "}
                {paid
                  ? "✓ Paid"
                  : busy
                    ? "Processing…"
                    : `Pending — Pay ₹${fmt(amount)}`}
              </span>
            </button>
          );
        })}
      </div>

      {/* Inline notice */}
      {notice && (
        <div style={{
          padding: "5px 12px",
          borderRadius: "6px",
          background: notice.ok ? "#e8f5e9" : "#ffebee",
          color:      notice.ok ? "#2e7d32" : "#c62828",
          fontFamily: "Montserrat-Regular",
          fontSize:   "0.75rem",
          display:    "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <span>{notice.text}</span>
          <button
            onClick={() => setNotice(null)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "inherit", fontSize: "0.9rem", lineHeight: 1, padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionBanner;
